import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ChevronLeft,
  CheckCircle2,
  ClipboardList,
  Clock3,
  DollarSign,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Search,
  Send,
  X,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import {
  btnPrimary,
  btnSecondary,
  inputCls,
  labelCls,
  pageSubtitleCls,
  pageTitleCls,
  selectCls,
  tableWrapCls,
  tdCls,
  thCls,
  theadCls,
  trCls,
} from '../../components/ui';

type EstadoBoleta = 'borrador' | 'pendiente' | 'revisada' | 'aprobada' | 'rechazada' | 'anulada';
type ModalidadLabor = 'hora' | 'fijo' | 'tarea' | 'produccion';

interface Finca { id: string; nombre: string; codigo?: string | null }
interface Lote { id: string; finca_id: string; numero: string; nombre: string; area_ha?: number | null }
interface Bloque { id: string; lote_id: string; num: string; area_ha?: number | null; plant_count?: number | null; estadio_vfp?: string | null }
interface Labor {
  id: string;
  codigo_vfp: string;
  nombre: string;
  modalidad_default: ModalidadLabor;
  unidad_default: string;
  tarifa_base: number | null;
  requiere_bloque: boolean;
  requiere_estadio: boolean;
  requiere_gps: boolean;
  requiere_maquinaria?: boolean;
}
interface Colaborador {
  id: number;
  nombre_completo: string;
  numero_empleado?: string | null;
  identificacion?: string | null;
}
type TipoEquipo = 'propio' | 'alquilado';

interface MaquinariaRow {
  id: string;
  boleta_id: string;
  equipo_codigo: string | null;
  equipo_nombre: string | null;
  operador_nombre: string | null;
  horas_equipo: number | null;
  tarifa_hora: number | null;
  combustible_l: number | null;
  costo_equipo: number | null;
  tipo_equipo: TipoEquipo | null;
  created_at: string;
}

const ESTADO_LABEL: Record<EstadoBoleta, string> = {
  borrador: 'Borrador',
  pendiente: 'Pendiente',
  revisada: 'Revisada',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
  anulada: 'Anulada',
};

const EQUIPOS_BASE = [
  'Tractor',
  'Backhoe',
  'Excavadora',
  'Chapulin',
  'Carreta',
  'Boom',
  'Montacargas',
  'Niveladora',
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

function fmtCRC(v?: number | null) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return 'CRC ' + n.toLocaleString('es-CR', { maximumFractionDigits: 0 });
}

function fmtDate(s?: string | null) {
  if (!s) return '-';
  return new Date(s).toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function weekLabel(fecha: string) {
  const d = new Date(`${fecha}T00:00:00`);
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${String(week).padStart(2, '0')}-${String(d.getFullYear()).slice(-2)}`;
}

async function getGps(): Promise<{ lat: number; lng: number; accuracy: number }> {
  if (!('geolocation' in navigator)) throw new Error('Este dispositivo no soporta GPS.');
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(new Error(err.message || 'No se pudo leer el GPS.')),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
    );
  });
}

function estadoBadge(estado: EstadoBoleta) {
  const color = estado === 'pendiente' ? 'var(--tone-info-text)'
    : estado === 'aprobada' ? 'var(--tone-success-text)'
      : estado === 'rechazada' || estado === 'anulada' ? '#f87171'
        : 'var(--ink-faint)';
  return (
    <span className="inline-flex rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color, background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}>
      {ESTADO_LABEL[estado]}
    </span>
  );
}

function Kpi({ label, value, icon, tone }: { label: string; value: string | number; icon: React.ReactNode; tone: string }) {
  return (
    <div className="rounded-lg border px-4 py-3" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
      <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider" style={{ color: tone }}>
        {icon}
        {label}
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color: 'var(--ink)' }}>{value}</div>
    </div>
  );
}

function MaquinariaForm({
  empresaId,
  supervisorNombre,
  fincas,
  lotes,
  bloques,
  labores,
  colaboradores,
  onClose,
  onSaved,
  onError,
}: {
  empresaId: number;
  supervisorNombre: string;
  fincas: Finca[];
  lotes: Lote[];
  bloques: Bloque[];
  labores: Labor[];
  colaboradores: Colaborador[];
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}) {
  const [fecha, setFecha] = useState(today());
  const [fincaId, setFincaId] = useState('');
  const [loteId, setLoteId] = useState('');
  const [bloqueIds, setBloqueIds] = useState<Set<string>>(new Set());
  const [laborId, setLaborId] = useState('');
  const [laborTexto, setLaborTexto] = useState('');
  const [showLaborOptions, setShowLaborOptions] = useState(false);
  const [equipoNombre, setEquipoNombre] = useState('');
  const [equipoCodigo, setEquipoCodigo] = useState('');
  const [showEquipoOptions, setShowEquipoOptions] = useState(false);
  const [tipoEquipo, setTipoEquipo] = useState<TipoEquipo | ''>('');
  const [operadorTexto, setOperadorTexto] = useState('');
  const [showOperadorOptions, setShowOperadorOptions] = useState(false);
  const [horometroInicio, setHorometroInicio] = useState('');
  const [horometroFin, setHorometroFin] = useState('');
  const [horasEquipo, setHorasEquipo] = useState('');
  const [tarifaHora, setTarifaHora] = useState('');
  const [combustible, setCombustible] = useState('');
  const [observacion, setObservacion] = useState('');
  const [gpsInicio, setGpsInicio] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [gpsFin, setGpsFin] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [gpsLoading, setGpsLoading] = useState<'inicio' | 'fin' | null>(null);

  const finca = fincas.find((f) => f.id === fincaId);
  const lote = lotes.find((l) => l.id === loteId);
  const lotesF = fincaId ? lotes.filter((l) => l.finca_id === fincaId) : lotes;
  const bloquesL = loteId ? bloques.filter((b) => b.lote_id === loteId) : [];
  const bloquesSel = bloquesL.filter((b) => bloqueIds.has(b.id));
  const estadio = bloquesSel.find((b) => b.estadio_vfp)?.estadio_vfp || '';
  const labor = labores.find((l) => l.id === laborId);
  const horasCalc = useMemo(() => {
    const manual = Number(horasEquipo || 0);
    if (manual > 0) return manual;
    const ini = Number(horometroInicio || 0);
    const fin = Number(horometroFin || 0);
    return fin > ini ? fin - ini : 0;
  }, [horasEquipo, horometroInicio, horometroFin]);
  const costoEquipo = horasCalc * Number(tarifaHora || 0);

  const laboresFiltradas = useMemo(() => {
    const terms = laborTexto.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const base = labores.filter((l) => {
      const machineLike = l.requiere_maquinaria || /tractor|maquin|equipo|excav|backhoe|boom|transporte|chapia|chapul/i.test(l.nombre);
      if (!machineLike) return false;
      if (!terms.length) return true;
      const haystack = `${l.codigo_vfp} ${l.nombre}`.toLowerCase();
      return terms.every((term) => haystack.includes(term));
    });
    return base.slice(0, 8);
  }, [laborTexto, labores]);

  const equiposFiltrados = useMemo(() => {
    const term = equipoNombre.trim().toLowerCase();
    return EQUIPOS_BASE.filter((x) => !term || x.toLowerCase().includes(term)).slice(0, 8);
  }, [equipoNombre]);

  const operadoresFiltrados = useMemo(() => {
    const terms = operadorTexto.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const base = terms.length
      ? colaboradores.filter((c) => {
        const codigo = c.numero_empleado || c.identificacion || String(c.id);
        const haystack = `${codigo} ${c.nombre_completo}`.toLowerCase();
        return terms.every((term) => haystack.includes(term));
      })
      : colaboradores;
    return base.slice(0, 8);
  }, [operadorTexto, colaboradores]);

  useEffect(() => {
    if (!labor) return;
    setTarifaHora(String(labor.tarifa_base ?? 0));
  }, [labor]);

  function elegirLabor(l: Labor) {
    setLaborTexto(`${l.codigo_vfp} - ${l.nombre}`);
    setLaborId(l.id);
    setShowLaborOptions(false);
  }

  function elegirOperador(c: Colaborador) {
    setOperadorTexto(c.nombre_completo);
    setShowOperadorOptions(false);
  }

  function toggleBloque(id: string) {
    setBloqueIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleTodosBloques() {
    setBloqueIds((prev) => prev.size === bloquesL.length ? new Set() : new Set(bloquesL.map((b) => b.id)));
  }

  async function capturarGps(tipo: 'inicio' | 'fin') {
    setGpsLoading(tipo);
    try {
      const p = await getGps();
      if (tipo === 'inicio') setGpsInicio(p);
      else setGpsFin(p);
    } catch (e: any) {
      onError(e?.message || 'No se pudo leer el GPS.');
    } finally {
      setGpsLoading(null);
    }
  }

  async function guardar(estado: EstadoBoleta) {
    if (!fecha || !finca || !lote || !labor || !equipoNombre.trim()) {
      onError('Fecha, finca, lote, labor y equipo son requeridos.');
      return;
    }
    if (!tipoEquipo) {
      onError('Indique si el equipo es propio o alquilado.');
      return;
    }
    if (labor.requiere_bloque && bloqueIds.size === 0) {
      onError('Esta labor requiere al menos un bloque.');
      return;
    }
    if (labor.requiere_gps && !gpsInicio) {
      onError('Esta labor requiere GPS de inicio.');
      return;
    }

    setSaving(true);
    const { data: nextNum, error: eNum } = await supabase.rpc('campo_next_boleta', { p_empresa_id: empresaId });
    if (eNum) {
      setSaving(false);
      onError(eNum.message);
      return;
    }

    const boleta = {
      empresa_id: empresaId,
      numero: String(nextNum || ''),
      fecha,
      semana: weekLabel(fecha),
      supervisor_nombre: supervisorNombre || null,
      finca_id: finca.id,
      finca_nombre: finca.nombre,
      lote_id: lote.id,
      lote_num: lote.numero,
      estadio_codigo: estadio || null,
      estado,
      inicio_at: gpsInicio ? new Date().toISOString() : null,
      inicio_lat: gpsInicio?.lat ?? null,
      inicio_lng: gpsInicio?.lng ?? null,
      inicio_precision: gpsInicio?.accuracy ?? null,
      fin_at: gpsFin ? new Date().toISOString() : null,
      fin_lat: gpsFin?.lat ?? null,
      fin_lng: gpsFin?.lng ?? null,
      fin_precision: gpsFin?.accuracy ?? null,
      costo_total: costoEquipo,
      observacion: observacion.trim() || null,
    };

    const { data: inserted, error: eIns } = await supabase.from('campo_boletas').insert(boleta).select('id').single();
    if (eIns || !inserted) {
      setSaving(false);
      onError(eIns?.message || 'No se pudo guardar la boleta.');
      return;
    }
    const boletaId = inserted.id;
    const bloqueRows = bloquesSel.map((b) => ({
      empresa_id: empresaId,
      boleta_id: boletaId,
      bloque_id: b.id,
      bloque_num: b.num,
      area_ha: b.area_ha ?? null,
      plantas: b.plant_count ?? null,
    }));
    const gpsRows = [
      gpsInicio && { empresa_id: empresaId, boleta_id: boletaId, lat: gpsInicio.lat, lng: gpsInicio.lng, accuracy_m: gpsInicio.accuracy, tipo: 'inicio' },
      gpsFin && { empresa_id: empresaId, boleta_id: boletaId, lat: gpsFin.lat, lng: gpsFin.lng, accuracy_m: gpsFin.accuracy, tipo: 'fin' },
    ].filter(Boolean);

    const [rLabor, rBloques, rMaq, rGps] = await Promise.all([
      supabase.from('campo_boleta_labores').insert([{
        empresa_id: empresaId,
        boleta_id: boletaId,
        labor_id: labor.id,
        labor_nombre: labor.nombre,
        modalidad: 'hora',
        unidad: 'hora_equipo',
        cantidad: horasCalc,
        horas: horasCalc,
        tarifa: Number(tarifaHora || 0),
        costo_labor: 0,
        distribucion_costo: 'area',
        observacion: 'Labor con maquinaria',
      }]),
      bloqueRows.length ? supabase.from('campo_boleta_bloques').insert(bloqueRows) : Promise.resolve({ error: null }),
      supabase.from('campo_boleta_maquinaria').insert([{
        empresa_id: empresaId,
        boleta_id: boletaId,
        equipo_codigo: equipoCodigo.trim() || null,
        equipo_nombre: equipoNombre.trim(),
        tipo_equipo: tipoEquipo || null,
        operador_nombre: operadorTexto.trim() || null,
        horometro_inicio: Number(horometroInicio || 0) || null,
        horometro_fin: Number(horometroFin || 0) || null,
        horas_equipo: horasCalc,
        tarifa_hora: Number(tarifaHora || 0),
        combustible_l: Number(combustible || 0),
        costo_equipo: costoEquipo,
        observacion: observacion.trim() || null,
      }]),
      gpsRows.length ? supabase.from('campo_boleta_gps').insert(gpsRows as any[]) : Promise.resolve({ error: null }),
    ]);

    setSaving(false);
    const err = rLabor.error || rBloques.error || rMaq.error || rGps.error;
    if (err) {
      onError(err.message);
      return;
    }
    onSaved();
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_0.85fr]">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Fecha</label>
            <input type="date" className={inputCls} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Supervisor</label>
            <input className={inputCls} value={supervisorNombre || 'Usuario autenticado'} readOnly />
          </div>
          <div>
            <label className={labelCls}>Estado</label>
            <input className={inputCls} value="Captura maquinaria" readOnly />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Finca</label>
            <select className={selectCls} value={fincaId} onChange={(e) => { setFincaId(e.target.value); setLoteId(''); setBloqueIds(new Set()); }}>
              <option value="">Seleccione finca</option>
              {fincas.map((f) => <option key={f.id} value={f.id}>{f.codigo ? `${f.codigo} - ` : ''}{f.nombre}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Lote</label>
            <select className={selectCls} value={loteId} onChange={(e) => { setLoteId(e.target.value); setBloqueIds(new Set()); }}>
              <option value="">Seleccione lote</option>
              {lotesF.map((l) => <option key={l.id} value={l.id}>Lote {l.numero} {l.nombre ? `- ${l.nombre}` : ''}</option>)}
            </select>
          </div>
        </div>

        {bloquesL.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <label className={labelCls}>Bloques</label>
              <button type="button" className="text-xs font-semibold" style={{ color: 'var(--emp-accent-txt)' }} onClick={toggleTodosBloques}>
                {bloqueIds.size === bloquesL.length ? 'Desmarcar todos' : 'Marcar todos'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {bloquesL.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => toggleBloque(b.id)}
                  className="rounded border px-3 py-2 text-xs font-semibold"
                  style={{
                    background: bloqueIds.has(b.id) ? 'var(--emp-accent-bg)' : 'var(--surface-overlay)',
                    borderColor: bloqueIds.has(b.id) ? 'var(--emp-accent)' : 'var(--line)',
                    color: bloqueIds.has(b.id) ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                  }}
                >
                  Bloque {b.num}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>Labor de maquinaria</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5" size={15} style={{ color: 'var(--ink-faint)' }} />
              <input
                className={`${inputCls} pl-9 pr-10`}
                style={{
                  background: labor ? 'var(--emp-accent-bg)' : 'var(--surface-overlay)',
                  borderColor: labor ? 'var(--emp-accent)' : 'var(--line)',
                  color: labor ? 'var(--emp-accent-txt)' : 'var(--ink)',
                }}
                value={laborTexto}
                onChange={(e) => { setLaborTexto(e.target.value); setLaborId(''); setShowLaborOptions(true); }}
                onFocus={() => setShowLaborOptions(true)}
                onBlur={() => window.setTimeout(() => setShowLaborOptions(false), 140)}
                placeholder="Buscar labor con maquinaria"
                spellCheck={false}
              />
              {laborTexto && (
                <button type="button" className="absolute right-2 top-2 rounded border p-1" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }} onClick={() => { setLaborTexto(''); setLaborId(''); }}>
                  <X size={14} />
                </button>
              )}
              {showLaborOptions && (
                <div className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border p-1 shadow-xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
                  {laboresFiltradas.length ? laboresFiltradas.map((l) => (
                    <button key={l.id} type="button" className="flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-xs" style={{ color: 'var(--ink)' }} onMouseDown={(e) => e.preventDefault()} onClick={() => elegirLabor(l)}>
                      <span><span className="font-mono font-semibold" style={{ color: 'var(--accent)' }}>{l.codigo_vfp}</span><span className="ml-2 font-semibold">{l.nombre}</span></span>
                      <span className="rounded border px-2 py-0.5 text-[10px] uppercase" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>Maq.</span>
                    </button>
                  )) : <div className="px-3 py-2 text-xs" style={{ color: 'var(--ink-faint)' }}>Sin coincidencias.</div>}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls}>Equipo</label>
            <div className="relative">
              <input
                className={`${inputCls} pr-10`}
                value={equipoNombre}
                onChange={(e) => { setEquipoNombre(e.target.value); setShowEquipoOptions(true); }}
                onFocus={() => setShowEquipoOptions(true)}
                onBlur={() => window.setTimeout(() => setShowEquipoOptions(false), 140)}
                placeholder="Tractor, Backhoe, Excavadora"
                spellCheck={false}
              />
              {equipoNombre && <button type="button" className="absolute right-2 top-2 rounded border p-1" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }} onClick={() => { setEquipoNombre(''); setEquipoCodigo(''); }}><X size={14} /></button>}
              {showEquipoOptions && (
                <div className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-lg border p-1 shadow-xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
                  {equiposFiltrados.map((e) => (
                    <button key={e} type="button" className="block w-full rounded px-3 py-2 text-left text-xs font-semibold" style={{ color: 'var(--ink)' }} onMouseDown={(ev) => ev.preventDefault()} onClick={() => { setEquipoNombre(e); setShowEquipoOptions(false); }}>{e}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className={labelCls}>Codigo equipo</label>
            <input className={inputCls} value={equipoCodigo} onChange={(e) => setEquipoCodigo(e.target.value)} placeholder="Opcional" />
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>Tipo de equipo</label>
            <div className="flex gap-2">
              {(['propio', 'alquilado'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTipoEquipo(t)}
                  className="flex-1 rounded border px-3 py-2 text-xs font-semibold capitalize"
                  style={{
                    background: tipoEquipo === t ? 'var(--emp-accent-bg)' : 'var(--surface-overlay)',
                    borderColor: tipoEquipo === t ? 'var(--emp-accent)' : 'var(--line)',
                    color: tipoEquipo === t ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>Operador</label>
            <div className="relative">
              <input
                className={`${inputCls} pr-10`}
                value={operadorTexto}
                onChange={(e) => { setOperadorTexto(e.target.value); setShowOperadorOptions(true); }}
                onFocus={() => setShowOperadorOptions(true)}
                onBlur={() => window.setTimeout(() => setShowOperadorOptions(false), 140)}
                placeholder="Buscar operador"
                spellCheck={false}
              />
              {operadorTexto && <button type="button" className="absolute right-2 top-2 rounded border p-1" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }} onClick={() => setOperadorTexto('')}><X size={14} /></button>}
              {showOperadorOptions && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border p-1 shadow-xl" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
                  {operadoresFiltrados.length ? operadoresFiltrados.map((c) => {
                    const codigo = c.numero_empleado || c.identificacion || String(c.id);
                    return (
                      <button key={c.id} type="button" className="flex w-full items-center gap-3 rounded px-3 py-2 text-left text-xs" style={{ color: 'var(--ink)' }} onMouseDown={(e) => e.preventDefault()} onClick={() => elegirOperador(c)}>
                        <span className="font-mono font-semibold" style={{ color: 'var(--accent)' }}>{codigo}</span>
                        <span className="font-semibold">{c.nombre_completo}</span>
                      </button>
                    );
                  }) : <div className="px-3 py-2 text-xs" style={{ color: 'var(--ink-faint)' }}>Sin coincidencias.</div>}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls}>Horometro inicial</label>
            <input type="number" step="0.01" className={inputCls} value={horometroInicio} onChange={(e) => setHorometroInicio(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Horometro final</label>
            <input type="number" step="0.01" className={inputCls} value={horometroFin} onChange={(e) => setHorometroFin(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Horas maquina</label>
            <input type="number" step="0.01" className={inputCls} value={horasEquipo || String(horasCalc || '')} onChange={(e) => setHorasEquipo(e.target.value)} placeholder="Auto por horometro" />
          </div>
          <div>
            <label className={labelCls}>Tarifa por hora</label>
            <input type="number" step="0.01" className={inputCls} value={tarifaHora} onChange={(e) => setTarifaHora(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Combustible litros</label>
            <input type="number" step="0.01" className={inputCls} value={combustible} onChange={(e) => setCombustible(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Costo estimado</label>
            <input className={inputCls} value={fmtCRC(costoEquipo)} readOnly />
          </div>
        </div>

        <div>
          <label className={labelCls}>Observacion</label>
          <textarea className={`${inputCls} min-h-20`} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
        </div>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border p-4" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <div className="mb-3 text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>GPS</div>
          <div className="grid grid-cols-1 gap-2">
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={() => capturarGps('inicio')} disabled={!!gpsLoading}>
              {gpsLoading === 'inicio' ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
              Inicio {gpsInicio ? `(${Math.round(gpsInicio.accuracy)} m)` : ''}
            </button>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={() => capturarGps('fin')} disabled={!!gpsLoading}>
              {gpsLoading === 'fin' ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
              Fin {gpsFin ? `(${Math.round(gpsFin.accuracy)} m)` : ''}
            </button>
          </div>
        </div>

        <div className="rounded-lg border p-4" style={{ background: 'var(--surface-raised)', borderColor: 'var(--line)' }}>
          <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>Resumen</div>
          <div className="mt-3 space-y-2 text-sm" style={{ color: 'var(--ink-muted)' }}>
            <div className="flex justify-between gap-3"><span>Horas maquina</span><strong className="font-mono" style={{ color: 'var(--ink)' }}>{horasCalc.toFixed(2)}</strong></div>
            <div className="flex justify-between gap-3"><span>Tarifa</span><strong className="font-mono" style={{ color: 'var(--ink)' }}>{fmtCRC(Number(tarifaHora || 0))}</strong></div>
            <div className="flex justify-between gap-3"><span>Combustible</span><strong className="font-mono" style={{ color: 'var(--ink)' }}>{Number(combustible || 0).toFixed(2)} L</strong></div>
            <div className="border-t pt-3" style={{ borderColor: 'var(--line)' }}>
              <div className="font-mono text-3xl font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(costoEquipo)}</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button type="button" className={btnSecondary} onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={() => guardar('borrador')} disabled={saving}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Guardar borrador
          </button>
          <button type="button" className={`${btnPrimary} flex items-center justify-center gap-2`} onClick={() => guardar('pendiente')} disabled={saving}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Enviar a revision
          </button>
        </div>
      </div>
    </div>
  );
}

export function BoletasMaquinariaPage({ supervisorNombre = '' }: { supervisorNombre?: string }) {
  const empresaId = useEmpresaId();
  const [rows, setRows] = useState<MaquinariaRow[]>([]);
  const [fincas, setFincas] = useState<Finca[]>([]);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [bloques, setBloques] = useState<Bloque[]>([]);
  const [labores, setLabores] = useState<Labor[]>([]);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [capturando, setCapturando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    const [{ data: m, error: eM }, { data: f }, { data: l }, { data: bl }, { data: lb }, { data: cols }] = await Promise.all([
      supabase
        .from('campo_boleta_maquinaria')
        .select('id, boleta_id, equipo_codigo, equipo_nombre, operador_nombre, horas_equipo, tarifa_hora, combustible_l, costo_equipo, tipo_equipo, created_at')
        .eq('empresa_id', empresaId)
        .order('created_at', { ascending: false })
        .limit(200),
      supabase.from('emp_fincas').select('id, nombre, codigo').eq('empresa_id', empresaId).eq('activo', true).order('nombre'),
      supabase.from('emp_lotes').select('id, finca_id, numero, nombre, area_ha').eq('empresa_id', empresaId).eq('activo', true).order('numero'),
      supabase.from('emp_bloques').select('id, lote_id, num, area_ha, plant_count, estadio_vfp').eq('empresa_id', empresaId).eq('activo', true).order('num'),
      supabase
        .from('campo_labores')
        .select('id, codigo_vfp, nombre, modalidad_default, unidad_default, tarifa_base, requiere_bloque, requiere_estadio, requiere_gps, requiere_maquinaria')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('codigo_vfp'),
      supabase
        .from('pl_colaboradores')
        .select('id, nombre_completo, numero_empleado, identificacion')
        .eq('empresa_id', empresaId)
        .in('estado', ['activo', 'vacaciones', 'incapacitado'])
        .order('nombre_completo'),
    ]);
    if (eM) setError(eM.message);
    setRows((m ?? []) as MaquinariaRow[]);
    setFincas((f ?? []) as Finca[]);
    setLotes((l ?? []) as Lote[]);
    setBloques((bl ?? []) as Bloque[]);
    setLabores((lb ?? []) as Labor[]);
    setColaboradores((cols ?? []) as Colaborador[]);
    setLoading(false);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const filtradas = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => [r.equipo_codigo, r.equipo_nombre, r.operador_nombre].some((x) => String(x ?? '').toLowerCase().includes(term)));
  }, [rows, q]);

  const stats = useMemo(() => ({
    registros: rows.length,
    horas: rows.reduce((s, r) => s + Number(r.horas_equipo ?? 0), 0),
    combustible: rows.reduce((s, r) => s + Number(r.combustible_l ?? 0), 0),
    costo: rows.reduce((s, r) => s + Number(r.costo_equipo ?? 0), 0),
  }), [rows]);

  async function onSaved() {
    setCapturando(false);
    await cargar();
  }

  if (capturando) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={pageTitleCls}>Nueva boleta de maquinaria</h1>
              <p className={pageSubtitleCls}>Captura amplia de equipo, operador, horometro, horas maquina, lote y GPS</p>
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={() => setCapturando(false)}>
              <ChevronLeft size={14} />
              Volver
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 sm:p-5">
          {error && (
            <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
              <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
            </div>
          )}
          <MaquinariaForm
            empresaId={empresaId}
            supervisorNombre={supervisorNombre}
            fincas={fincas}
            lotes={lotes}
            bloques={bloques}
            labores={labores}
            colaboradores={colaboradores}
            onClose={() => setCapturando(false)}
            onSaved={onSaved}
            onError={setError}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 px-5 py-4" style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className={pageTitleCls}>Boletas de Maquinaria</h1>
            <p className={pageSubtitleCls}>Captura separada de labores con equipo, horometro y horas maquina</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label className={labelCls}>Buscar</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-faint)' }} />
                <input className={`${inputCls} pl-8 sm:w-72`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Equipo u operador" />
              </div>
            </div>
            <button type="button" className={`${btnSecondary} flex items-center justify-center gap-2`} onClick={cargar} disabled={loading}>
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Recargar
            </button>
            <button type="button" className={`${btnPrimary} flex items-center justify-center gap-2`} onClick={() => setCapturando(true)} disabled={labores.length === 0}>
              <Plus size={14} />
              Nueva maquinaria
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-5">
        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm" style={{ background: 'var(--tone-warn-bg)', borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-ink)' }}>
            <AlertTriangle size={17} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
            <span>{error}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi label="Registros" value={stats.registros} icon={<ClipboardList size={14} />} tone="var(--emp-accent-txt)" />
          <Kpi label="Horas maq." value={stats.horas.toFixed(2)} icon={<Clock3 size={14} />} tone="var(--tone-info-text)" />
          <Kpi label="Combustible" value={`${stats.combustible.toFixed(1)} L`} icon={<MapPin size={14} />} tone="var(--tone-warn-text)" />
          <Kpi label="Costo" value={fmtCRC(stats.costo)} icon={<DollarSign size={14} />} tone="var(--tone-success-text)" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={18} className="animate-spin" />
            Cargando maquinaria...
          </div>
        ) : filtradas.length === 0 ? (
          <div className="rounded-lg border border-dashed px-5 py-12 text-center" style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
            Sin registros de maquinaria.
          </div>
        ) : (
          <div className={tableWrapCls}>
            <table className="w-full border-collapse text-xs">
              <thead className={theadCls}>
                <tr>
                  <th className={thCls}>Fecha</th>
                  <th className={thCls}>Equipo</th>
                  <th className={thCls}>Tipo</th>
                  <th className={thCls}>Operador</th>
                  <th className={`${thCls} text-right`}>Horas</th>
                  <th className={`${thCls} text-right`}>Tarifa</th>
                  <th className={`${thCls} text-right`}>Comb.</th>
                  <th className={`${thCls} text-right`}>Costo</th>
                  <th className={thCls}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((r) => (
                  <tr key={r.id} className={trCls}>
                    <td className={tdCls}>{fmtDate(r.created_at)}</td>
                    <td className={tdCls}>
                      <div className="font-semibold" style={{ color: 'var(--ink)' }}>{r.equipo_nombre || '-'}</div>
                      <div className="font-mono" style={{ color: 'var(--ink-faint)' }}>{r.equipo_codigo || '-'}</div>
                    </td>
                    <td className={tdCls}>
                      {r.tipo_equipo ? (
                        <span className="inline-flex rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-muted)', background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}>
                          {r.tipo_equipo}
                        </span>
                      ) : (
                        <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>Sin definir</span>
                      )}
                    </td>
                    <td className={tdCls}>{r.operador_nombre || '-'}</td>
                    <td className={`${tdCls} text-right font-mono`}>{Number(r.horas_equipo ?? 0).toFixed(2)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{fmtCRC(r.tarifa_hora)}</td>
                    <td className={`${tdCls} text-right font-mono`}>{Number(r.combustible_l ?? 0).toFixed(1)} L</td>
                    <td className={`${tdCls} text-right font-mono font-bold`} style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(r.costo_equipo)}</td>
                    <td className={tdCls}>{estadoBadge('pendiente')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default BoletasMaquinariaPage;
